import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import MuiAutocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { SelectableObject } from "src/components/Form/types";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export interface CheckboxAutocompleteProps<T extends SelectableObject> {
  label: string;
  items: T[];
  selected: T[];
  onChange: (value: T[]) => void;
  showSelectAll?: boolean;
  error?: string;
  sx?: Record<string, any>;
}

export function CheckboxAutocomplete<T extends SelectableObject>({
  label,
  items,
  selected,
  onChange,
  showSelectAll = true,
  error,
  sx,
}: CheckboxAutocompleteProps<T>): ReactNode {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [sortedItems, setSortedItems] = useState<T[]>(items);

  const getOptionLabel = (option: T) => {
    return option?.getDisplayName ? option.getDisplayName(t) : String(option);
  };

  const allOption = {
    getId: () => "__select_all__",
    getDisplayName: () => t("multiselect.select_all"),
  } as T;

  const handleOpen = () => {
    const selectedIds = new Set(selected.map((item) => item.getId()));
    const selectedItems = items.filter((item) => selectedIds.has(item.getId()));
    const unselectedItems = items.filter((item) => !selectedIds.has(item.getId()));
    setSortedItems([...selectedItems, ...unselectedItems]);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const displayItems = inputValue === "" ? sortedItems : items;

  const optionsWithAll =
    showSelectAll && inputValue === "" && items.length > 0 ? [allOption, ...displayItems] : displayItems;

  const handleChange = (_event: any, newValue: T[]) => {
    const allOptionSelected =
      showSelectAll && inputValue === "" && newValue.find((option) => option.getId() === "__select_all__");

    if (allOptionSelected) {
      onChange(selected.length === items.length ? [] : items);
    } else {
      onChange(newValue.filter((option) => option.getId() !== "__select_all__"));
    }
  };

  const normalizeString = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filterOptions = createFilterOptions<T>({
    stringify: (option) => normalizeString(getOptionLabel(option)),
  });

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && inputValue.trim() !== "") {
      event.preventDefault();
      const normalizedInputValue = normalizeString(inputValue);
      const filteredOptions = items.filter((item) =>
        normalizeString(getOptionLabel(item)).includes(normalizedInputValue),
      );
      if (filteredOptions.length > 0) {
        const selectedIds = new Set(selected.map((item) => item.getId()));
        const newItems = filteredOptions.filter((item) => !selectedIds.has(item.getId()));
        onChange([...selected, ...newItems]);
        setInputValue("");
        setOpen(false);
      }
    }
  };

  const getDisplayValue = () => {
    if (selected.length === 0) return "";
    if (selected.length === 1) return getOptionLabel(selected[0]);
    if (selected.length === items.length) return t("multiselect.all_selected");
    return t("multiselect.n_selected", { count: selected.length });
  };

  return (
    <>
      <MuiAutocomplete
        multiple
        size="small"
        options={optionsWithAll}
        value={selected}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue, reason) => {
          if (reason === "reset" || reason === "selectOption" || reason === "removeOption") return;
          setInputValue(newInputValue);
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        disableCloseOnSelect
        disabled={items.length === 0}
        noOptionsText={t("multiselect.no_options")}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => option.getId() === value.getId()}
        filterOptions={filterOptions}
        renderOption={(props, option, { selected: isSelected }) => {
          const isAllOption = option.getId() === "__select_all__";
          const allSelected = selected.length === items.length;
          const someSelected = selected.length > 0 && selected.length < items.length;
          const checked = isAllOption ? allSelected : isSelected;
          return (
            <li
              {...props}
              key={option.getId()}
              style={{
                ...props.style,
                transition: "all 0.25s ease-out",
                backgroundColor: checked ? "rgba(25, 118, 210, 0.08)" : undefined,
              }}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={checked}
                indeterminate={isAllOption && someSelected}
                sx={{
                  transition: "all 0.25s ease-out",
                  "&.Mui-checked": { transform: "scale(1.1)" },
                  "&:active": { transform: "scale(0.95)" },
                }}
              />
              <span
                style={{
                  fontWeight: isAllOption ? 600 : undefined,
                  transition: "color 0.25s ease-out",
                  color: checked ? "rgba(25, 118, 210, 0.9)" : undefined,
                }}
              >
                {getOptionLabel(option)}
              </span>
            </li>
          );
        }}
        renderValue={() => null}
        renderInput={(params) => {
          const displayValue = getDisplayValue();
          const renderedValue = open ? inputValue : displayValue;
          const hasRenderedValue = renderedValue !== "";
          return (
            <TextField
              {...params}
              label={label}
              placeholder={t("multiselect.search_placeholder")}
              onKeyDown={handleKeyDown}
              slotProps={{
                ...params.slotProps,
                inputLabel: { ...params.slotProps.inputLabel, shrink: hasRenderedValue || undefined },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  value: renderedValue,
                  style: {
                    ...params.slotProps.htmlInput.style,
                    color: !open && hasRenderedValue ? "rgba(0, 0, 0, 0.87)" : undefined,
                  },
                },
              }}
            />
          );
        }}
        sx={sx}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
}

export default CheckboxAutocomplete;
